/**
 * MarketPickerModal — Iter-191
 *
 * Reusable, mobile-first, searchable multi-select for metro markets.
 * Talent picks one or more markets and designates ONE as primary (star).
 * Auto-suggest based on city + state is fetched on open.
 *
 * Props:
 *   open                 boolean
 *   onClose              () => void
 *   initialMarketIds     string[]
 *   initialPrimaryId     string | null
 *   suggestCity          string (optional, for auto-suggest hint)
 *   suggestState         string (optional, for auto-suggest hint)
 *   onSave               async ({ market_ids, primary_market_id }) => void
 *   allowRequestUnlisted boolean  (default true — talent can flag "not listed")
 *   onRequestUnlisted    async ({ requested_city, requested_state, note }) => void
 *   title                string (default "Choose your markets")
 *   description          string
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Star, MapPin, Check, Search, Plus, Sparkles, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

export default function MarketPickerModal({
  open,
  onClose,
  initialMarketIds = [],
  initialPrimaryId = null,
  suggestCity = '',
  suggestState = '',
  onSave,
  allowRequestUnlisted = true,
  onRequestUnlisted,
  title = 'Choose your markets',
  description = 'Pick the metro regions you regularly work in. Star one as your primary market.',
}) {
  const [allMarkets, setAllMarkets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [primaryId, setPrimaryId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [suggested, setSuggested] = useState(null);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [requestCity, setRequestCity] = useState('');
  const [requestState, setRequestState] = useState('');
  const [requestNote, setRequestNote] = useState('');
  const [submittingRequest, setSubmittingRequest] = useState(false);

  // Reset state on open
  useEffect(() => {
    if (!open) return;
    setSelectedIds(initialMarketIds || []);
    setPrimaryId(initialPrimaryId || (initialMarketIds && initialMarketIds[0]) || null);
    setSearch('');
    setShowRequestForm(false);
    setRequestCity(suggestCity || '');
    setRequestState(suggestState || '');
    setRequestNote('');
    (async () => {
      setLoading(true);
      try {
        const res = await api.get('/markets', { params: { limit: 200 } });
        setAllMarkets(Array.isArray(res.data) ? res.data : []);
      } catch {
        toast.error('Failed to load markets');
      } finally {
        setLoading(false);
      }
      // Auto-suggest
      if (suggestCity || suggestState) {
        try {
          const sres = await api.get('/markets/suggest', {
            params: { city: suggestCity || undefined, state: suggestState || undefined },
          });
          setSuggested(sres.data?.market || null);
        } catch {
          setSuggested(null);
        }
      } else {
        setSuggested(null);
      }
    })();
  }, [open, initialMarketIds, initialPrimaryId, suggestCity, suggestState]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allMarkets;
    return allMarkets.filter((m) => {
      if (m.metro_name.toLowerCase().includes(q)) return true;
      if ((m.states_covered || []).some((s) => s.toLowerCase().includes(q))) return true;
      if ((m.cities_covered || []).some((c) => c.toLowerCase().includes(q))) return true;
      return false;
    });
  }, [allMarkets, search]);

  const toggleMarket = (id) => {
    setSelectedIds((cur) => {
      if (cur.includes(id)) {
        const next = cur.filter((x) => x !== id);
        // If the removed market was primary, demote primary to first remaining.
        if (primaryId === id) setPrimaryId(next[0] || null);
        return next;
      }
      const next = [...cur, id];
      if (!primaryId) setPrimaryId(id);
      return next;
    });
  };

  const setPrimary = (id) => {
    if (!selectedIds.includes(id)) {
      // Adding a market via star auto-selects it too.
      setSelectedIds((cur) => [...cur, id]);
    }
    setPrimaryId(id);
  };

  const addSuggested = () => {
    if (!suggested) return;
    if (!selectedIds.includes(suggested.id)) {
      setSelectedIds((cur) => [...cur, suggested.id]);
      if (!primaryId) setPrimaryId(suggested.id);
    }
  };

  const handleSave = async () => {
    if (selectedIds.length === 0) {
      toast.error('Please select at least one market, or request a new one.');
      return;
    }
    setSaving(true);
    try {
      await onSave({
        market_ids: selectedIds,
        primary_market_id: primaryId || selectedIds[0],
      });
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save markets');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitRequest = async () => {
    if (!requestCity && !requestState) {
      toast.error('Tell us the city or state you work in.');
      return;
    }
    setSubmittingRequest(true);
    try {
      if (onRequestUnlisted) {
        await onRequestUnlisted({
          requested_city: requestCity || null,
          requested_state: requestState || null,
          note: requestNote || null,
        });
      } else {
        await api.post('/talent/profile/markets/request', {
          requested_city: requestCity || null,
          requested_state: requestState || null,
          note: requestNote || null,
        });
      }
      toast.success('Thanks! We will review and add your market.');
      setShowRequestForm(false);
      setRequestNote('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmittingRequest(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        className="max-w-lg sm:max-w-xl w-[96vw] p-0 gap-0 max-h-[92vh] flex flex-col"
        data-testid="market-picker-modal"
      >
        <DialogHeader className="px-5 pt-5 pb-3 border-b">
          <DialogTitle className="text-base sm:text-lg flex items-center gap-2">
            <MapPin className="w-5 h-5 text-cyan-500" /> {title}
          </DialogTitle>
          <DialogDescription className="text-xs sm:text-sm">{description}</DialogDescription>
        </DialogHeader>

        <div className="px-4 sm:px-5 pt-3 pb-2 border-b bg-gray-50">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search markets, states, or cities…"
              className="pl-9 h-10 bg-white"
              data-testid="market-search-input"
            />
          </div>
          {suggested && !selectedIds.includes(suggested.id) && (
            <button
              type="button"
              onClick={addSuggested}
              className="mt-3 w-full text-left rounded-lg border border-cyan-200 bg-cyan-50 hover:bg-cyan-100 px-3 py-2 flex items-center gap-2 transition"
              data-testid="market-suggested-add"
            >
              <Sparkles className="w-4 h-4 text-cyan-600 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-xs uppercase tracking-wide text-cyan-700 font-semibold">Suggested for you</div>
                <div className="text-sm text-gray-900 truncate">{suggested.metro_name}</div>
              </div>
              <Plus className="w-4 h-4 text-cyan-700" />
            </button>
          )}
          {selectedIds.length > 0 && (
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <span className="text-xs text-gray-500">{selectedIds.length} selected:</span>
              {selectedIds.slice(0, 4).map((id) => {
                const m = allMarkets.find((x) => x.id === id);
                if (!m) return null;
                const isPrimary = id === primaryId;
                return (
                  <Badge
                    key={id}
                    className={`text-[11px] font-normal ${isPrimary ? 'bg-amber-100 text-amber-800 border border-amber-200' : 'bg-white text-gray-700 border'}`}
                  >
                    {isPrimary && <Star className="w-3 h-3 mr-1 fill-amber-500 text-amber-500" />}
                    {m.metro_name}
                  </Badge>
                );
              })}
              {selectedIds.length > 4 && (
                <span className="text-xs text-gray-500">+{selectedIds.length - 4} more</span>
              )}
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-2 sm:px-3 py-2 min-h-[260px]">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-gray-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading markets…
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-10 px-4 text-sm text-gray-500">
              <MapPin className="w-6 h-6 mx-auto mb-2 text-gray-300" />
              No markets match "{search}".
            </div>
          ) : (
            <ul className="space-y-1">
              {filtered.map((m) => {
                const sel = selectedIds.includes(m.id);
                const isPrimary = m.id === primaryId;
                return (
                  <li key={m.id}>
                    <div
                      className={`group flex items-center gap-2 rounded-lg px-3 py-2.5 transition cursor-pointer ${
                        sel ? 'bg-cyan-50 ring-1 ring-cyan-200' : 'hover:bg-gray-50'
                      }`}
                      onClick={() => toggleMarket(m.id)}
                      data-testid={`market-row-${m.slug}`}
                    >
                      <div
                        className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 ${
                          sel ? 'bg-cyan-500 border-cyan-500 text-white' : 'border-gray-300 bg-white'
                        }`}
                      >
                        {sel && <Check className="w-3.5 h-3.5" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {m.metro_name}
                          {isPrimary && (
                            <span className="ml-2 text-[10px] font-semibold uppercase text-amber-700 bg-amber-100 rounded px-1.5 py-0.5 align-middle">
                              Primary Market
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-gray-500 truncate">
                          {(m.states_covered || []).join(' · ')}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setPrimary(m.id); }}
                        className={`p-1.5 rounded-full transition ${
                          isPrimary
                            ? 'text-amber-500'
                            : 'text-gray-300 hover:text-amber-400'
                        }`}
                        aria-label={isPrimary ? 'Primary market' : 'Set as primary market'}
                        data-testid={`market-star-${m.slug}`}
                      >
                        <Star className={`w-5 h-5 ${isPrimary ? 'fill-amber-400' : ''}`} />
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {allowRequestUnlisted && (
          <div className="px-4 sm:px-5 py-3 border-t bg-gray-50">
            {!showRequestForm ? (
              <button
                type="button"
                onClick={() => setShowRequestForm(true)}
                className="text-xs text-cyan-700 hover:text-cyan-900 underline underline-offset-2"
                data-testid="market-request-toggle"
              >
                My market isn't listed → request one
              </button>
            ) : (
              <div className="space-y-2" data-testid="market-request-form">
                <div className="flex gap-2">
                  <Input
                    value={requestCity}
                    onChange={(e) => setRequestCity(e.target.value)}
                    placeholder="City"
                    className="h-9 text-sm"
                    data-testid="market-request-city"
                  />
                  <Input
                    value={requestState}
                    onChange={(e) => setRequestState(e.target.value.toUpperCase().slice(0, 2))}
                    placeholder="ST"
                    className="h-9 text-sm w-20"
                    data-testid="market-request-state"
                  />
                </div>
                <Input
                  value={requestNote}
                  onChange={(e) => setRequestNote(e.target.value)}
                  placeholder="Note (optional)"
                  className="h-9 text-sm"
                  data-testid="market-request-note"
                />
                <div className="flex gap-2 justify-end">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowRequestForm(false)}
                    className="text-xs"
                  >Cancel</Button>
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleSubmitRequest}
                    disabled={submittingRequest}
                    className="bg-cyan-600 hover:bg-cyan-700 text-white text-xs"
                    data-testid="market-request-submit"
                  >
                    {submittingRequest ? 'Submitting…' : 'Submit request'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="px-4 sm:px-5 py-3 border-t bg-white flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={onClose}
            className="text-sm text-gray-600 hover:text-gray-900"
            data-testid="market-picker-cancel"
          >
            <X className="w-4 h-4 inline-block mr-1" /> Cancel
          </button>
          <Button
            onClick={handleSave}
            disabled={saving || selectedIds.length === 0}
            className="bg-cyan-600 hover:bg-cyan-700 text-white h-10 px-5"
            data-testid="market-picker-save"
          >
            {saving ? 'Saving…' : `Save ${selectedIds.length || ''} market${selectedIds.length === 1 ? '' : 's'}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
